import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchTempleById } from "../redux/templeSlice";
import { getTempleImageUrl } from "../utils/imageHelper";

/* ── Styled placeholder for temples without real photos ── */
const ImagePlaceholder = ({ name, height = "500px" }) => (
  <div
    className="rounded shadow"
    style={{
      width: "85%",
      height,
      margin: "0 auto",
      background: "linear-gradient(135deg, #f5f0e8 0%, #e8ddd0 100%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      color: "#8b7355",
      fontSize: "18px",
      fontWeight: "500"
    }}
  >
    <span style={{ fontSize: "48px", marginBottom: "10px" }}>🛕</span>
    <span>{name || "Image Coming Soon"}</span>
  </div>
);

function TempleDetail() {

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { temple } = useSelector(
    (state) => state.temples
  );

  useEffect(() => {
    dispatch(fetchTempleById(id));
  }, [dispatch, id]);

  if (!temple)
    return <h3 className="text-center mt-5">Loading...</h3>;

  /* ---------------- DEFAULT VALUES ---------------- */

  const history =
    temple.history ||
    "This sacred temple holds deep spiritual significance for devotees. Though detailed historical records are not available at the moment, devotees believe the temple has been a place of divine blessings and faith for generations.";

  const timings =
    temple.timings ||
    "Temple is generally open from early morning till evening. Devotees are advised to visit during daytime for darshan.";

  const abhishekDetails =
    temple.abhishekDetails ||
    "Special abhishek rituals are performed regularly. Devotees can contact temple authorities for participation details.";

  const poojaDetails =
    temple.poojaDetails ||
    "Daily pooja and aarti are conducted with devotion. Please contact temple management for special pooja bookings.";

  const bhandaraDetails =
    temple.bhandaraYatraDetails ||
    "Bhandara and religious gatherings are organized during special occasions and festivals.";

  const bhojanDetails =
    temple.bhojanPrasadi ||
    "Prasadi distribution may be available on special days. Kindly check with temple management for availability.";

  const trustees =
    temple.trustees && temple.trustees.length > 0
      ? temple.trustees
      : [
          {
            name: "Temple Management",
            position: "Administration",
            contact: "Please visit temple for contact details"
          }
        ];

  const location =
    temple.location ||
    "Temple location details will be updated soon.";

  /* ---------------- GOOGLE MAP ---------------- */

  const openGoogleMap = () => {

    if (!temple.location) {
      alert("Location information not available currently.");
      return;
    }

    navigator.geolocation.getCurrentPosition(

      (position) => {

        const origin =
          `${position.coords.latitude},${position.coords.longitude}`;

        const destination =
          encodeURIComponent(temple.location);

        const googleUrl =
          `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;

        window.open(googleUrl, "_blank");

      },

      () => {

        const destination =
          encodeURIComponent(temple.location);

        const googleUrl =
          `https://www.google.com/maps/search/?api=1&query=${destination}`;

        window.open(googleUrl, "_blank");

      }

    );
  };

  const imgUrl = getTempleImageUrl(temple.image);

  return (

    <div className="container mt-4 mb-5">

      <h2 className="text-center fw-bold mb-3 text-primary">
         {temple.name}
      </h2>

      <div className="text-center mb-4">

        {imgUrl ? (
          <img
            src={imgUrl}
            alt={temple.name}
            className="rounded shadow"
            style={{
              width: "85%",
              height: "500px",
              objectFit: "contain",
              backgroundColor: "#f8f9fa"
            }}
          />
        ) : (
          <ImagePlaceholder name={temple.name} height="500px" />
        )}

      </div>

      <div className="card shadow p-4 mb-4">

        <div className="row text-center">

          <div className="col-md-3">
            <h6> Location</h6>
            <span
              style={{
                color: "blue",
                cursor: "pointer",
                textDecoration: "underline"
              }}
              onClick={() =>
                navigate(`/map/${temple._id}`)
              }
            >
              {location}
            </span>

            <button
              className="btn btn-sm btn-success mt-2"
              onClick={openGoogleMap}
            >
              Open in Google Maps
            </button>
          </div>

          <div className="col-md-3">
            <h6> Deity</h6>
            <p>{temple.deity || "Information will be updated soon."}</p>
          </div>

          <div className="col-md-3">
            <h6>🕉 Category</h6>
            <p>{temple.category || "Spiritual Place"}</p>
          </div>

          <div className="col-md-3">
            <h6> Timings</h6>
            <p>{timings}</p>
          </div>

        </div>

      </div>

      <div className="card shadow p-4 mb-4">
        <h4 className="text-center text-warning">
           Short Information
        </h4>
        <p>{history}</p>
      </div>

      <div className="row">

        <div className="col-md-6 mb-3">
          <div className="card shadow p-3 h-100">
            <h5 className="text-success"> Abhishek Details</h5>
            <p>{abhishekDetails}</p>
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className="card shadow p-3 h-100">
            <h5 className="text-success"> Pooja Details</h5>
            <p>{poojaDetails}</p>
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className="card shadow p-3 h-100">
            <h5 className="text-danger"> Major Festivals</h5>
            <p>{bhandaraDetails}</p>
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className="card shadow p-3 h-100">
            <h5 className="text-info"> Bhojan Prasadi</h5>
            <p>{bhojanDetails}</p>
          </div>
        </div>

      </div>

      <div className="card shadow p-4 mt-4">

        <h4 className="text-center text-primary mb-3">
          👥 Trustees
        </h4>

        <div className="table-responsive">

          <table className="table table-bordered table-striped text-center">

            <thead className="table-dark">
              <tr>
                <th>Name</th>
                <th>Position</th>
                <th>Contact</th>
              </tr>
            </thead>

            <tbody>
              {trustees.map((t, i) => (
                <tr key={i}>
                  <td>{t.name}</td>
                  <td>{t.position}</td>
                  <td>{t.contact}</td>
                </tr>
              ))}
            </tbody>

          </table>

        </div>

      </div>

      {/* Gallery Button */}
      <div className="text-center mt-4">
        <button
          className="btn btn-warning btn-lg shadow"
          onClick={() => navigate(`/gallery/${temple._id}`)}
        >
           View Photos & Videos of {temple.name}
        </button>
      </div>
      
    </div>

  );

}

export default TempleDetail;